/**
 * Serialisering av energikalkylatorns indata till/från query-parametrar (bokmärke / delning).
 */

import type { CaffeineFilterMode, EnergySport } from "./energyCalculator";
import {
  DEFAULT_PRERACE_MG_PER_KG,
  PRERACE_MG_PER_KG_MAX,
  PRERACE_MG_PER_KG_MIN,
  isEnergySport,
} from "./energyCalculator";
import {
  CYCLING_DISTANCE_PRESETS,
  DISTANCE_PRESET_CUSTOM,
  RUNNING_DISTANCE_PRESETS,
  SKIING_DISTANCE_PRESETS,
  TRIATHLON_DISTANCE_PRESETS,
  defaultDistancePresetId,
} from "./energyCalculatorDistances";

export type EnergyCalcUrlHydration = {
  distancePresetId?: string;
  customDistanceKm?: string;
  customTriSwimM?: string;
  customTriBikeKm?: string;
  customTriRunKm?: string;
  hours?: number;
  minutes?: number;
  timeSeconds?: number;
  carbsPerHour?: number;
  paceMinInput?: string;
  paceSecInput?: string;
  cyclingKmhInput?: string;
  triathlonSplitMode?: boolean;
  triSwimH?: number;
  triSwimM?: number;
  triBikeH?: number;
  triBikeM?: number;
  triRunH?: number;
  triRunM?: number;
  personSex?: "male" | "female";
  personAgeYears?: number;
  personHeightCm?: number;
  personWeightKg?: number;
  preraceMgPerKg?: number;
  /** Undvik att distansförinställning skriver över tid från URL */
  skipPresetTimeOnce?: boolean;
  skipPaceSyncOnce?: boolean;
  skipKmhSyncOnce?: boolean;
};

function isPresetIdForSport(sport: EnergySport, id: string): boolean {
  if (id === DISTANCE_PRESET_CUSTOM) return true;
  const list =
    sport === "triathlon"
      ? TRIATHLON_DISTANCE_PRESETS
      : sport === "cycling"
        ? CYCLING_DISTANCE_PRESETS
        : sport === "running"
          ? RUNNING_DISTANCE_PRESETS
          : SKIING_DISTANCE_PRESETS;
  return list.some((p) => p.id === id);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function parseIntSafe(raw: string | null, fallback: number): number {
  if (raw == null || raw === "") return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseFloatSafe(raw: string | null, fallback: number): number {
  if (raw == null || raw === "") return fallback;
  const n = parseFloat(raw.replace(",", "."));
  return Number.isFinite(n) ? n : fallback;
}

/** Stabil jämförelse / delning */
export function energyCalcSearchParamsToStableString(sp: URLSearchParams): string {
  return Array.from(sp.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
}

export function parseEnergyCalcUrlParams(
  sp: URLSearchParams,
  sport: EnergySport
): EnergyCalcUrlHydration {
  const out: EnergyCalcUrlHydration = {};

  const dist = sp.get("dist");
  if (dist != null && dist.length > 0) {
    if (isPresetIdForSport(sport, dist)) {
      out.distancePresetId = dist;
    } else {
      out.distancePresetId = defaultDistancePresetId(sport);
    }
  }

  const dkm = sp.get("dkm");
  if (dkm != null) out.customDistanceKm = dkm;

  const tswim = sp.get("tswim");
  if (tswim != null) out.customTriSwimM = tswim;
  const tbike = sp.get("tbike");
  if (tbike != null) out.customTriBikeKm = tbike;
  const trun = sp.get("trun");
  if (trun != null) out.customTriRunKm = trun;

  const hasMainClock =
    sp.has("th") || sp.has("tm") || sp.has("ts") || sp.has("tsec");
  const hasTriSplitClock =
    sp.has("tsh") ||
    sp.has("tsm") ||
    sp.has("tbh") ||
    sp.has("tbm") ||
    sp.has("trh") ||
    sp.has("trm");

  const tsplitRaw = sp.get("tsplit");
  let triUseSplitClocks: boolean | null = null;
  if (sport === "triathlon") {
    if (tsplitRaw === "0") triUseSplitClocks = false;
    else if (tsplitRaw === "1") triUseSplitClocks = true;
    else if (hasTriSplitClock) triUseSplitClocks = true;
    else if (hasMainClock || sp.has("tsec")) triUseSplitClocks = false;
    if (triUseSplitClocks !== null) {
      out.triathlonSplitMode = triUseSplitClocks;
    }
  }

  if (hasMainClock || hasTriSplitClock) {
    out.skipPresetTimeOnce = true;
  }

  const useTriSplitOnly =
    sport === "triathlon" && triUseSplitClocks === true && hasTriSplitClock;

  const tsec = sp.get("tsec");
  if (!useTriSplitOnly && tsec != null && tsec !== "") {
    const total = Math.max(0, Math.floor(parseFloatSafe(tsec, 0)));
    out.hours = Math.floor(total / 3600);
    out.minutes = Math.floor((total % 3600) / 60);
    out.timeSeconds = total % 60;
    out.skipPresetTimeOnce = true;
  } else if (!useTriSplitOnly) {
    if (sp.has("th")) out.hours = clamp(parseIntSafe(sp.get("th"), 0), 0, 72);
    if (sp.has("tm"))
      out.minutes = clamp(parseIntSafe(sp.get("tm"), 0), 0, 59);
    if (sp.has("ts"))
      out.timeSeconds = clamp(parseIntSafe(sp.get("ts"), 0), 0, 59);
  }

  const cph = sp.get("cph");
  if (cph != null && cph !== "") {
    out.carbsPerHour = clamp(Math.round(parseFloatSafe(cph, 60)), 1, 200);
  }

  if (sp.has("pm") || sp.has("ps")) {
    out.skipPaceSyncOnce = true;
    if (sp.has("pm")) out.paceMinInput = String(clamp(parseIntSafe(sp.get("pm"), 5), 0, 180));
    if (sp.has("ps")) out.paceSecInput = String(clamp(parseIntSafe(sp.get("ps"), 0), 0, 59));
  }

  if (sp.has("kmh")) {
    out.skipKmhSyncOnce = true;
    out.cyclingKmhInput = String(
      Math.max(0, Math.round(parseFloatSafe(sp.get("kmh"), 30) * 10) / 10)
    );
  }

  if (hasTriSplitClock) {
    if (sp.has("tsh")) out.triSwimH = clamp(parseIntSafe(sp.get("tsh"), 0), 0, 48);
    if (sp.has("tsm")) out.triSwimM = clamp(parseIntSafe(sp.get("tsm"), 0), 0, 59);
    if (sp.has("tbh")) out.triBikeH = clamp(parseIntSafe(sp.get("tbh"), 0), 0, 48);
    if (sp.has("tbm")) out.triBikeM = clamp(parseIntSafe(sp.get("tbm"), 0), 0, 59);
    if (sp.has("trh")) out.triRunH = clamp(parseIntSafe(sp.get("trh"), 0), 0, 48);
    if (sp.has("trm")) out.triRunM = clamp(parseIntSafe(sp.get("trm"), 0), 0, 59);
  }

  const sex = sp.get("sex");
  if (sex === "m" || sex === "male") out.personSex = "male";
  else if (sex === "f" || sex === "female") out.personSex = "female";

  if (sp.has("age")) {
    out.personAgeYears = clamp(parseIntSafe(sp.get("age"), 30), 15, 90);
  }
  if (sp.has("hcm")) {
    out.personHeightCm = clamp(parseIntSafe(sp.get("hcm"), 180), 130, 220);
  }
  if (sp.has("wkg")) {
    out.personWeightKg = clamp(parseFloatSafe(sp.get("wkg"), 80), 35, 200);
  }

  if (sp.has("mgkg")) {
    out.preraceMgPerKg = clamp(
      Math.round(parseFloatSafe(sp.get("mgkg"), DEFAULT_PRERACE_MG_PER_KG) * 10) / 10,
      PRERACE_MG_PER_KG_MIN,
      PRERACE_MG_PER_KG_MAX
    );
  }

  return out;
}

export type EnergyCalcUrlSnapshot = {
  sport: EnergySport;
  caffeineMode: CaffeineFilterMode;
  includeBottles: boolean;
  distancePresetId: string;
  customDistanceKm: string;
  customTriSwimM: string;
  customTriBikeKm: string;
  customTriRunKm: string;
  hours: number;
  minutes: number;
  timeSeconds: number;
  carbsPerHour: number;
  paceMinInput: string;
  paceSecInput: string;
  cyclingKmhInput: string;
  triathlonSplitMode: boolean;
  triSwimH: number;
  triSwimM: number;
  triBikeH: number;
  triBikeM: number;
  triRunH: number;
  triRunM: number;
  personSex: "male" | "female";
  personAgeYears: number;
  personHeightCm: number;
  personWeightKg: number;
  preraceMgPerKg: number;
};

export function buildEnergyCalcSearchParams(s: EnergyCalcUrlSnapshot): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set("sport", s.sport);

  if (s.caffeineMode === "none") sp.set("caffeine", "none");
  if (!s.includeBottles) sp.set("bottles", "0");

  sp.set("dist", s.distancePresetId);

  if (s.distancePresetId === DISTANCE_PRESET_CUSTOM) {
    if (s.sport === "triathlon") {
      if (s.customTriSwimM !== "") sp.set("tswim", s.customTriSwimM);
      if (s.customTriBikeKm !== "") sp.set("tbike", s.customTriBikeKm);
      if (s.customTriRunKm !== "") sp.set("trun", s.customTriRunKm);
    } else {
      if (s.customDistanceKm !== "") sp.set("dkm", s.customDistanceKm);
    }
  }

  if (s.sport === "triathlon") {
    if (s.triathlonSplitMode) {
      sp.set("tsplit", "1");
      sp.set("tsh", String(s.triSwimH));
      sp.set("tsm", String(s.triSwimM));
      sp.set("tbh", String(s.triBikeH));
      sp.set("tbm", String(s.triBikeM));
      sp.set("trh", String(s.triRunH));
      sp.set("trm", String(s.triRunM));
    } else {
      sp.set("tsplit", "0");
      sp.set("th", String(s.hours));
      sp.set("tm", String(s.minutes));
      sp.set("ts", String(s.timeSeconds));
    }
  } else {
    sp.set("th", String(s.hours));
    sp.set("tm", String(s.minutes));
    sp.set("ts", String(s.timeSeconds));
  }

  sp.set("cph", String(s.carbsPerHour));

  if (s.sport === "running") {
    sp.set("pm", s.paceMinInput);
    sp.set("ps", s.paceSecInput);
  }
  if (s.sport === "cycling") {
    sp.set("kmh", s.cyclingKmhInput);
  }

  sp.set("sex", s.personSex === "male" ? "m" : "f");
  sp.set("age", String(s.personAgeYears));
  sp.set("hcm", String(s.personHeightCm));
  sp.set("wkg", String(s.personWeightKg));
  sp.set("mgkg", String(s.preraceMgPerKg));

  const sorted = new URLSearchParams();
  Array.from(sp.keys())
    .sort((a, b) => a.localeCompare(b))
    .forEach((k) => {
      const v = sp.get(k);
      if (v != null) sorted.set(k, v);
    });
  return sorted;
}

export function readSportFromSearchParams(sp: URLSearchParams): EnergySport {
  const p = sp.get("sport");
  return isEnergySport(p) ? p : "triathlon";
}
